// The call screen's one hook: owns the WebSocket to the Room DO, the local
// media stream, the RTCPeerConnection session, and the call state machine
// (packages/core's transition()), and exposes a small, render-friendly
// surface. Mutable objects (the socket, the session, the stream) live in
// refs; only what the screen actually renders is state.
//
// Reconnection (BRIEF 7.2): a dropped socket or a failed/disconnected
// RTCPeerConnection dispatches NETWORK_LOST, which moves the state machine
// into "reconnecting". A separate effect below watches that phase and
// drives the actual recovery — closing the stale session/socket and running
// the exact same `connectOnce()` used for the initial join, which republishes
// this client's current local tracks and re-pulls the room's current
// participants off the fresh `welcome` message. Nobody has to click
// anything: the effect re-arms itself on every `RECONNECT_FAILED` (with
// backoff) until either it succeeds or the state machine gives up after its
// own attempt cap, and a browser `online` event nudges it immediately rather
// than waiting out whatever backoff is left.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  INITIAL_CALL_STATE,
  handWentUp,
  ROOM_CLOSE_CODES,
  transition,
  type CallState,
  type ClientMessage,
  type KnockEntry,
  type ParticipantSnapshot,
  type RoomMessage,
  type RoomSnapshot,
} from "@breeze/core";
import {
  getBlurEnabled,
  getCameraEnabled,
  getMicEnabled,
  getOrCreateDeviceId,
  getRejoinToken,
  getUserKey,
  setBlurEnabled,
  setRejoinToken,
} from "../lib/storage";
import { blurSupported, createBlur, type BlurHandle } from "../lib/blur";
import {
  createOffTrack,
  hasMultipleCameras,
  mediaProblemText,
  type OffTrack,
  openCallMedia,
  openCamera,
  openScreenShare,
  stopStream,
  supportsScreenShare,
  type CameraHandles,
} from "../lib/media";
import {
  CallSession,
  fetchIceServers,
  preferredRidForTileCount,
  TracksNotSendingError,
  type MediaCounters,
  type MediaKind,
  type PullRequest,
} from "../lib/peer";
import { HEAR_OK, healthReport, hearStep, micDead, type HearWatch } from "../lib/mediaHealth";
import type { TriviaEvent } from "../components/WaitingTrivia";
import type { VideoFitReport } from "../components/VideoView";
import { PING_ASSISTANT_MS, PING_MS, RoomSocket, roomWsUrl, type RoomKind } from "../lib/wsClient";
import { leaveContext, noteRecent, type RecentEvent } from "../lib/leaveContext";
import { usePanel, type PanelStore } from "./usePanel";
import { useChat, type ChatStore } from "./useChat";
import { useAssistant, type AssistantStore } from "./useAssistant";
import type { RemoteTile } from "../types";

export interface CallControls {
  readonly micOn: boolean;
  readonly cameraOn: boolean;
  readonly originalSoundOn: boolean;
  /** This person's hand is up. */
  readonly handRaised: boolean;
  readonly sharingScreen: boolean;
  readonly canShareScreen: boolean;
  readonly canFlipCamera: boolean;
  /** Background blur is on for the camera. */
  readonly blurOn: boolean;
  /** This browser can draw the blur at all. */
  readonly canBlur: boolean;
  /** The blur is starting: its model is loading and no blurred frame is out
   * yet. */
  readonly blurLoading: boolean;
}

/** A locally-mutable mirror of media.ts's CameraHandles — the flip-camera
 * path needs to swap `videoTrack` in place after the initial open. */
interface MutableCameraHandles {
  stream: MediaStream;
  audioTrack: MediaStreamTrack | null;
  videoTrack: MediaStreamTrack | null;
}

export type FlipPhase = "idle" | "out" | "in";

/** Matches the breeze-flip-out and breeze-flip-in keyframes in global.css. */
const FLIP_OUT_MS = 170;
const FLIP_IN_MS = 260;
/** How long a mic or camera may stay muted with the page in front before it
 * counts as taken (a phone call), and how often that is checked. */
const QUIET_LOST_MS = 3000;
const MEDIA_CHECK_MS = 2000;
/** A first join still on "Connecting" this long reports how far it got. */
const JOIN_SLOW_MS = 15_000;
/** How long a person whose sound and picture are here waits for the room to
 * say the other side has this side's too (QUEUE row 472) before showing. */
const REVEAL_WAIT_MS = 6_000;

/** A phone or tablet, where a page put away is a page the person is not
 * using: a phone call, another app. */
function onPhone(): boolean {
  return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
}

export interface UseCallStoreResult {
  readonly state: CallState;
  readonly room: RoomSnapshot | null;
  /** The people this side shows yet (QUEUE row 472). */
  readonly revealed: ReadonlySet<string>;
  /** A tile has someone's sound and picture: tell the room. */
  readonly noteMediaReady: (id: string) => void;
  readonly selfId: string | null;
  readonly selfStream: MediaStream | null;
  readonly screenStream: MediaStream | null;
  readonly remoteTiles: readonly RemoteTile[];
  readonly pendingKnocks: readonly KnockEntry[];
  readonly controls: CallControls;
  readonly toggleMic: () => void;
  readonly toggleCamera: () => void;
  readonly toggleOriginalSound: () => void;
  /** Raise or lower this person's own hand. */
  readonly toggleHand: () => void;
  /** Host only: lower someone else's hand. */
  readonly lowerHand: (participantId: string) => void;
  readonly toggleScreenShare: () => void;
  readonly respondToKnock: (knockId: string, allow: boolean) => void;
  readonly flipCamera: () => void;
  readonly toggleBlur: () => void;
  /** Which camera is live. Only the front camera's self view is mirrored. */
  readonly facing: "user" | "environment";
  /** The self view's turn during a flip: "out" turns it edge-on while the
   * cameras swap, "in" turns the new camera into view. */
  readonly flipPhase: FlipPhase;
  readonly leave: () => void;
  /** BRIEF 9/19: off by default, host-only. The host's own tap flips it at
   * once (Adam, 2026-09-28: "the recording thing should appear instantly");
   * a start the room turns down flips it back and says why. Everyone else
   * sees the Room's confirmed state. */
  readonly recording: boolean;
  /** When this client was let into the room (ms since epoch); null until then.
   * The call screen counts elapsed time from it. */
  readonly joinedAt: number | null;
  /** The owner joined with Record on and it has not started yet (it waits
   * for a second person sending media). Any start or stop clears it. */
  readonly recordArmed: boolean;
  /** Host only: the call is being recorded and the room has not been told
   * (the operator's default). `recording` stays false until he taps Record,
   * which tells everyone and shows the indicator. */
  readonly recordingQuiet: boolean;
  /** Stops the quiet recording and deletes what it has, for a guest who said
   * no. */
  readonly deleteQuietRecording: () => void;
  readonly toggleRecording: () => void;
  /** The popup after the host stops a recording: saved, with a trash
   * button, or deleted once the trash was tapped. */
  readonly recordingSaved: RecordingSavedState | null;
  readonly discardRecording: () => void;
  readonly holdRecordingSaved: (held: boolean) => void;
  /** A short line the call screen shows for a few seconds: a request the
   * room turned down (a recording that could not start). */
  readonly notice: string | null;
  readonly showNotice: (text: string) => void;
  /** A line that stays up while a call's sound is broken: someone this side
   * cannot hear, someone who cannot hear this side, or a microphone sending
   * silence. With a button when a tap can fix it. */
  readonly mediaAlert: MediaAlert | null;
  /** The door's trivia card, for the host's log: what this guest was shown
   * and answered while waiting (QUEUE row 416). */
  readonly reportTrivia: (event: TriviaEvent) => void;
  /** How WebKit laid out this person's own picture, for the drops log
   * (QUEUE row 445). */
  readonly reportSelfView: (report: VideoFitReport) => void;
  /** The Breeze panel: shared notes, questions, and next steps, and this
   * person's private notes. */
  readonly panel: PanelStore;
  /** The in-call text chat (mostly links). */
  readonly chat: ChatStore;
  /** Breeze's live assistant: on or off, this person's consent, what only the
   * host sees. */
  readonly assistant: AssistantStore;
}

export interface MediaAlert {
  readonly text: string;
  readonly action?: { readonly label: string; readonly run: () => void };
}

export interface RecordingSavedState {
  /** Null when the room had not confirmed the recording yet, so there is
   * nothing to name for deletion. */
  readonly recordingId: string | null;
  readonly phase: "saved" | "deleting" | "deleted";
}

const SAVED_POPUP_MS = 2200;
const DELETED_POPUP_MS = 1400;

export interface CallOptions {
  /** Take this account's seat over from another device (a join with
   * `move=1`): the room sees the same participant, not a leave and a join. */
  readonly move?: boolean;
  /** The owner asked for this call to be recorded (the pre-join Record
   * button). Sent on every connect of this call, so a reconnect before the
   * second person arrives keeps the choice. */
  readonly record?: boolean;
}

/** A drop in progress, for the drop log (worker/src/drops.ts). */
interface DropNote {
  readonly at: number;
  readonly trigger: "socket" | "media" | "offline";
  readonly code?: number;
  readonly hidden: boolean;
  attempts: number;
  readonly selfId: string | null;
}

/** What this client tells the room about a drop once it knows how it ended. */
function dropReportOf(note: DropNote, outcome: "restored" | "new_seat" | "lobby" | "gave_up"): Record<string, unknown> {
  return {
    type: "dropReport",
    outcome,
    trigger: note.trigger,
    ...(note.code !== undefined ? { code: note.code } : {}),
    awayMs: Date.now() - note.at,
    attempts: note.attempts,
    ...(note.hidden ? { hidden: true } : {}),
    platform: "web",
  };
}

export function useCallStore(kind: RoomKind, slug: string, displayName: string, options: CallOptions = {}): UseCallStoreResult {
  const isDebug =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
  const [state, setState] = useState<CallState>(INITIAL_CALL_STATE);
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [selfId, setSelfId] = useState<string | null>(null);
  // Read inside handleRoomMessage to exclude self from participantJoined/
  // participantUpdated (see the fix note at those cases below) — a ref, not
  // the state, so the check is never one render behind the message that
  // needs it.
  const selfIdRef = useRef<string | null>(null);
  const [selfStream, setSelfStream] = useState<MediaStream | null>(null);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [flipPhase, setFlipPhase] = useState<FlipPhase>("idle");
  const flippingRef = useRef(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [remoteTiles, setRemoteTiles] = useState<Record<string, RemoteTile>>({});
  const [pendingKnocks, setPendingKnocks] = useState<readonly KnockEntry[]>([]);
  const [recording, setRecording] = useState(false);
  const [joinedAt, setJoinedAt] = useState<number | null>(null);
  const [recordArmed, setRecordArmed] = useState(false);
  const [recordingQuiet, setRecordingQuiet] = useState(false);
  const recordingIdRef = useRef<string | null>(null);
  const [recordingSaved, setRecordingSaved] = useState<RecordingSavedState | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedHeldRef = useRef(false);
  const closeSavedIn = useCallback((ms: number) => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => {
      if (savedHeldRef.current) return;
      setRecordingSaved(null);
    }, ms);
  }, []);
  const [notice, setNotice] = useState<string | null>(null);
  const [mediaAlert, setMediaAlert] = useState<MediaAlert | null>(null);
  // Who this side cannot hear (by participant id), and who has said they
  // cannot hear this side (by id, with their name). Read by the media
  // check every two seconds, which sets mediaAlert from them.
  const hearingRef = useRef(new Map<string, HearWatch>());
  const unheardByRef = useRef(new Map<string, string>());
  const mediaIssueRef = useRef<((id: string) => void) | null>(null);
  /** The host's live reading of each person's audio (row 506). */
  const audioDxRef = useRef(new Map<string, string>());
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showNotice = useCallback((text: string) => {
    setNotice(text);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4000);
  }, []);
  const [controls, setControls] = useState<CallControls>({
    micOn: getMicEnabled(),
    cameraOn: getCameraEnabled(),
    originalSoundOn: false,
    handRaised: false,
    sharingScreen: false,
    canShareScreen: supportsScreenShare(),
    canFlipCamera: false,
    blurOn: getBlurEnabled() && blurSupported(),
    canBlur: blurSupported(),
    blurLoading: false,
  });
  // The latest toggles, for callbacks that outlive a render (republishing
  // after a reconnect tells the room the current mute and camera state).
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const socketRef = useRef<RoomSocket | null>(null);
  const sessionRef = useRef<CallSession | null>(null);
  /** Mirrors `controls.originalSoundOn` for code that runs outside a render
   * (a reconnect's republish, the toggle's async republish). */
  const originalSoundOnRef = useRef(false);
  /** When this page last told the room each refused track name (row 473). */
  const trackMissingSentRef = useRef(new Map<string, number>());
  /** When this page last pushed each of its own kinds again because the
   * others could not pull it (row 473). */
  const republishedAtRef = useRef<Partial<Record<"audio" | "video", number>>>({});
  /** Original sound is one setting for the whole call: when someone else
   * turns it on or off, this mic follows (Adam, 2026-10-04: "original sound
   * should show as enabled for all particpants once someone puts it on").
   * Set once toggleOriginalSound exists; ignores a flip within a few seconds
   * of this client's own, so two people pressing at once cannot ping-pong. */
  const followOriginalSoundRef = useRef<(on: boolean) => void>(() => {});
  const lastSoundToggleRef = useRef(0);
  const cameraRef = useRef<MutableCameraHandles | null>(null);
  /** Set while the camera's background is blurred: the canvas track that is
   * published, over the raw camera track it reads from. */
  const blurRef = useRef<BlurHandle | null>(null);
  /** Lets go of the camera, the blur's drawing loop included. */
  /** Set once the call has let go of the camera, so a check still in
   * flight does not open it again. */
  const mediaReleasedRef = useRef(false);
  /** Set while the camera is off: the hardware camera is closed (so its light
   * is out) and this black track is what the call holds and publishes. */
  const offTrackRef = useRef<OffTrack | null>(null);
  /** True while the camera is being opened again, so a second tap waits. */
  const cameraOpeningRef = useRef(false);
  const releaseCamera = useCallback(() => {
    mediaReleasedRef.current = true;
    offTrackRef.current?.stop();
    offTrackRef.current = null;
    const blur = blurRef.current;
    if (blur) {
      blur.source.stop();
      blur.stop();
      blurRef.current = null;
    }
    stopStream(cameraRef.current?.stream);
  }, []);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const facingRef = useRef<"user" | "environment">("user");
  const midsRef = useRef<{ audio: string | null; video: string | null; screen: string | null }>({
    audio: null,
    video: null,
    screen: null,
  });
  /** Which (participant, kind) tracks this session has already pulled, keyed
   * by `${participantId}:${kind}` and holding the SFU sessionId/trackName we
   * pulled — so a `participantUpdated` broadcast can be diffed against what
   * is actually pulled rather than re-pulling (or missing) on every message.
   * A changed sessionId/trackName for an already-pulled key means the far
   * side republished (a reconnect, most commonly) and has to be re-pulled. */
  const pulledRef = useRef<Map<string, { sessionId: string; trackName: string }>>(new Map());
  /** Keys of (participant, kind) pulls in flight, so a watchdog tick or a
   * second broadcast never starts a second pull of the same track. */
  const pullingRef = useRef<Set<string>>(new Set());
  /** Consecutive failed pulls per participant, for the watchdog: the first
   * few are retried quietly, and a participant who stays blank is reported. */
  const pullFailsRef = useRef<Map<string, { fails: number; since: number; reported: boolean }>>(new Map());
  /** The remote tiles as of the last render, for the watchdog's timer. */
  const remoteTilesRef = useRef<Record<string, RemoteTile>>({});
  /** The last-known `originalSound` flag we've seen broadcast for each
   * remote participant, so a `participantUpdated` handler can tell "this
   * flag just flipped" apart from "this snapshot repeats what we already
   * knew" — needed because toggling original sound never changes a
   * participant's (sessionId, trackName), so `syncParticipantTracks`'s own
   * diff is a no-op for it and something else has to notice the flip. */
  const originalSoundRef = useRef<Map<string, boolean>>(new Map());
  /** The last `handRaisedAt` seen for each remote participant, so a raise can
   * be told from an update that left the hand where it was. */
  const handsRef = useRef<Map<string, number | undefined>>(new Map());
  const remoteTileCountRef = useRef(0);
  const videoInRef = useRef("");
  const reconnectInFlightRef = useRef(false);
  /** Set when the connection being opened reuses the live media session
   * (the socket was lost, the media never was); the next welcome reads it. */
  const keptSessionRef = useRef(false);
  /** The people this side shows, once the room said both sides have each
   * other's sound and picture (QUEUE row 472), and the ones it told the room
   * it has, each with its fallback timer. Kept across a reconnect that kept
   * the seat. */
  const revealedRef = useRef<Set<string>>(new Set());
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const mediaReadyRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const reveal = useCallback((id: string) => {
    const timer = mediaReadyRef.current.get(id);
    if (timer) clearTimeout(timer);
    if (revealedRef.current.has(id)) return;
    revealedRef.current.add(id);
    setRevealed(new Set(revealedRef.current));
  }, []);
  const noteMediaReady = useCallback(
    (id: string) => {
      if (mediaReadyRef.current.has(id) || revealedRef.current.has(id)) return;
      mediaReadyRef.current.set(id, setTimeout(() => reveal(id), REVEAL_WAIT_MS));
      socketRef.current?.sendLoose({ type: "mediaReady", id });
    },
    [reveal],
  );
  /** The drop in progress: what dropped, when, and the seat it was in.
   * Reported over the call's socket once the call is back, or by beacon
   * when the reconnect gives up. */
  const dropRef = useRef<DropNote | null>(null);
  /** The last few minutes of drops and media state changes, and when this
   * seat joined, for the leaving report (lib/leaveContext.ts). */
  const recentRef = useRef<RecentEvent[]>([]);
  const joinedAtRef = useRef<number | null>(null);
  // When each stage of the first join finished, in ms from the ask to join
  // (QUEUE row 468). Sent once the media connects, and early if the join is
  // still on "Connecting" after JOIN_SLOW_MS, as a `join` drops row.
  const joinMarksRef = useRef<{ t0: number; marks: Record<string, number>; slowSent: boolean; sent: boolean } | null>(null);
  const markJoin = useCallback((stage: string) => {
    const j = joinMarksRef.current;
    if (!j || j.sent || stage in j.marks) return;
    j.marks[stage] = Date.now() - j.t0;
  }, []);
  const sendJoinTiming = useCallback((slow: boolean) => {
    const j = joinMarksRef.current;
    if (!j || j.sent || (slow && j.slowSent)) return;
    socketRef.current?.sendLoose({ type: "joinTiming", stages: j.marks, slow, platform: "web" });
    if (slow) j.slowSent = true;
    else j.sent = true;
  }, []);
  const roomKey = `${kind}:${slug}`;
  const sendToRoom = useCallback((message: ClientMessage) => socketRef.current?.send(message), []);
  const panel = usePanel(roomKey, sendToRoom);
  const chat = useChat(sendToRoom);
  // Stable members only: the message handler is captured by the open socket.
  const { ingest: ingestPanel, requestPrivate: requestPanelPrivate } = panel;
  const { ingest: ingestChat } = chat;
  const sendAudio = useCallback((frame: ArrayBuffer) => socketRef.current?.sendBinary(frame), []);
  const assistantOnRef = useRef(false);
  const setAssistantPings = useCallback((on: boolean) => {
    assistantOnRef.current = on;
    socketRef.current?.setPingMs(on ? PING_ASSISTANT_MS : PING_MS);
  }, []);
  const micTrack = useMemo(() => selfStream?.getAudioTracks()[0] ?? null, [selfStream]);
  const remoteAudio = useMemo(
    () => Object.values(remoteTiles).flatMap((t) => (t.audioTrack ? [{ id: t.participant.id, track: t.audioTrack }] : [])),
    [remoteTiles],
  );
  const assistant = useAssistant(sendToRoom, sendAudio, micTrack, setAssistantPings, roomKey, remoteAudio);
  const { ingest: ingestAssistant } = assistant;

  const dispatch = useCallback((event: Parameters<typeof transition>[1]) => {
    setState((prev) => transition(prev, event));
  }, []);

  /** Starts the drop log's note for a seat that just dropped. Only a seat in
   * the call counts, and only the first sign of a drop. */
  const noteDrop = useCallback((trigger: "socket" | "media", code?: number) => {
    noteRecent(recentRef.current, `${trigger} lost${code !== undefined ? ` ${code}` : ""}`);
    markJoin("lost");
    if (dropRef.current || !selfIdRef.current) return;
    dropRef.current = {
      at: Date.now(),
      trigger: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : trigger,
      ...(code !== undefined ? { code } : {}),
      hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
      attempts: 0,
      selfId: selfIdRef.current,
    };
  }, []);

  const upsertRemoteTile = useCallback((participant: ParticipantSnapshot) => {
    setRemoteTiles((prev) => ({
      ...prev,
      [participant.id]: {
        participant,
        audioTrack: prev[participant.id]?.audioTrack ?? null,
        videoTrack: prev[participant.id]?.videoTrack ?? null,
        screenTrack: prev[participant.id]?.screenTrack ?? null,
      },
    }));
  }, []);

  const clearRemoteTrackKind = useCallback((participantId: string, kind: MediaKind) => {
    setRemoteTiles((prev) => {
      const existing = prev[participantId];
      if (!existing) return prev;
      const next: RemoteTile = { ...existing };
      if (kind === "audio") (next as { audioTrack: MediaStreamTrack | null }).audioTrack = null;
      if (kind === "video") (next as { videoTrack: MediaStreamTrack | null }).videoTrack = null;
      if (kind === "screen") (next as { screenTrack: MediaStreamTrack | null }).screenTrack = null;
      return { ...prev, [participantId]: next };
    });
  }, []);

  const clearParticipantPulls = useCallback((participantId: string) => {
    for (const key of [...pulledRef.current.keys()]) {
      if (key.startsWith(`${participantId}:`)) pulledRef.current.delete(key);
    }
  }, []);

  // The number of remote cameras on screen decides which simulcast layer to
  // ask for (BRIEF 7.1: one person full screen wants the top layer, a grid
  // tile a lower one). It is kept in a ref for the pulls made inside message
  // handlers, and every change re-asks the SFU for every camera already
  // pulled. Before, the layer was fixed at pull time from a count that lags
  // a render, so a rejoin seen while the old tile was still up left the one
  // remaining person on the bottom layer for the rest of the call (QUEUE row
  // 340). A screen share makes every camera a thumbnail, held on the middle
  // layer rather than the bottom one: climbing back from the bottom layer
  // after the share ends took about 13 seconds of soft video even on a fast
  // link, and from the middle one under a second (QUEUE row 442, "clay
  // stopped sharing and looked super blurry here for a while";
  // scripts/probe-share-stop.mjs). Four or more cameras still get the bottom.
  useEffect(() => {
    const tiles = Object.values(remoteTiles);
    const cameras = tiles.filter((t) => t.participant.tracks.video).length;
    const sharing = tiles.some((t) => t.participant.tracks.screen);
    remoteTilesRef.current = remoteTiles;
    remoteTileCountRef.current = sharing ? Math.max(cameras, 2) : cameras;
    void sessionRef.current?.setVideoLayer(preferredRidForTileCount(remoteTileCountRef.current));
  }, [remoteTiles]);

  // Debug-only (same `?debug=1` gate as window.__breezePc below): exposes the
  // live set of remote tile ids so scripts/e2e-call.mjs can prove a reconnect
  // never leaves a stray, never-cleaned-up tile behind for a bogus participant
  // — a plain remoteTileCountRef read wouldn't be enough since the whole bug
  // this proves the absence of is "one extra id nobody asked for," not just a
  // wrong count at one instant.
  useEffect(() => {
    if (!isDebug) return;
    (window as unknown as { __breezeRemoteTileIds?: string[] }).__breezeRemoteTileIds = Object.keys(remoteTiles);
  }, [remoteTiles, isDebug]);

  /** Pulls (or clears) whatever changed between what this session has
   * already pulled for `participant` and what their latest snapshot says is
   * published. Called on every `participantJoined`/`participantUpdated`/
   * `welcome`, so it has to be a genuine diff — re-pulling an unchanged
   * track on every mute-toggle broadcast would renegotiate for no reason,
   * and never noticing a changed trackName (a reconnect republish, a screen
   * share starting) would leave that tile silent or frozen.
   *
   * `pulledRef` is only marked for a (participant, kind) key AFTER
   * `pullMany` actually resolves, never before it's even attempted. An
   * earlier version marked it first (so a second, near-simultaneous
   * broadcast for the same participant wouldn't double-pull the same
   * kind) — but Cloudflare Realtime's API does occasionally 502 on a
   * pull/renegotiate (confirmed live during e2e testing: a transient 502
   * from our own /api/realtime/*\/renegotiate proxy, non-deterministic,
   * roughly 1 in 6 multi-participant test runs), and marking-before-calling
   * meant a failed pull was recorded as done and permanently un-retriable:
   * nothing else ever notices, the tile just never gets that participant's
   * media, and the only sign is a track that "connects" per the UI but
   * carries flat inbound-rtp bytes forever. Marking after success means a
   * failed pull naturally retries the next time this participant's
   * snapshot changes for any reason. */
  const syncParticipantTracks = useCallback(
    (session: CallSession, participant: ParticipantSnapshot): Promise<void> => {
      const kinds: readonly MediaKind[] = ["audio", "video", "screen"];
      const toPull: PullRequest[] = [];
      const toMark: { key: string; ref: { sessionId: string; trackName: string } }[] = [];
      for (const kind of kinds) {
        const key = `${participant.id}:${kind}`;
        const ref = participant.tracks[kind];
        const already = pulledRef.current.get(key);
        if (!ref) {
          if (already) {
            pulledRef.current.delete(key);
            clearRemoteTrackKind(participant.id, kind);
          }
          continue;
        }
        if (already && already.sessionId === ref.sessionId && already.trackName === ref.trackName) continue;
        if (pullingRef.current.has(key)) continue;
        toMark.push({ key, ref });
        toPull.push({
          participantId: participant.id,
          kind,
          remote: ref,
          preferredRid: kind === "video" ? preferredRidForTileCount(remoteTileCountRef.current) : undefined,
          originalSound: kind === "audio" ? participant.originalSound : undefined,
        });
      }
      if (toPull.length === 0) return Promise.resolve();
      for (const { key } of toMark) pullingRef.current.add(key);
      return session
        .pullMany(toPull)
        .then(
          () => {
            for (const { key, ref } of toMark) pulledRef.current.set(key, ref);
            pullFailsRef.current.delete(participant.id);
          },
          (err: unknown) => {
            // The kinds that did land stay pulled, so the sweep below asks
            // again only for the one the SFU keeps refusing, and does not
            // close and re-pull a picture that is already playing.
            if (err instanceof TracksNotSendingError) {
              const refused = new Set(err.requests.map((r) => r.remote.trackName));
              for (const { key, ref } of toMark) if (!refused.has(ref.trackName)) pulledRef.current.set(key, ref);
            }
            throw err;
          },
        )
        .finally(() => {
          for (const { key } of toMark) pullingRef.current.delete(key);
        });
    },
    [clearRemoteTrackKind],
  );

  /** `syncParticipantTracks`, but never rejects: a pull failure (confirmed
   * live as a transient 502 from Cloudflare Realtime's own API, not
   * something this client can prevent) is logged and left for the next
   * broadcast about this participant to retry — see the long comment on
   * `syncParticipantTracks` for why `pulledRef` staying unmarked is what
   * makes that retry possible. Every call site awaits this instead of the
   * throwing version, so one participant's transient pull failure can
   * never surface as an unhandled promise rejection or stop `Promise.all`
   * from finishing the rest of the room in `welcome`. */
  const syncParticipantTracksSafe = useCallback(
    (session: CallSession, participant: ParticipantSnapshot): Promise<void> =>
      syncParticipantTracks(session, participant).catch((err: unknown) => {
        console.error(`Failed to pull tracks for participant ${participant.id}:`, err);
        // A track the SFU accepted from them and then never registered stays
        // "not found" for everyone, however often it is pulled (QUEUE row
        // 473). Tell them through the room, which passes it on only while it
        // is still their current track, and their page pushes it again.
        if (err instanceof TracksNotSendingError) {
          for (const r of err.requests) {
            if (r.kind === "screen") continue;
            const was = trackMissingSentRef.current.get(r.remote.trackName);
            if (was !== undefined && Date.now() - was < 8000) continue;
            trackMissingSentRef.current.set(r.remote.trackName, Date.now());
            socketRef.current?.sendLoose({ type: "trackMissing", id: participant.id, kind: r.kind, trackName: r.remote.trackName });
          }
        }
        const prior = pullFailsRef.current.get(participant.id);
        pullFailsRef.current.set(participant.id, {
          fails: (prior?.fails ?? 0) + 1,
          since: prior?.since ?? Date.now(),
          reported: prior?.reported ?? false,
        });
      }),
    [syncParticipantTracks],
  );

  /** A published track this client has not pulled is a blank tile, and a
   * failed pull used to wait for the next broadcast about that person, which
   * may never come (QUEUE row 354: the host let a guest in and saw an empty
   * tile for the rest of the call). Every few seconds, retry any remote
   * participant whose published track has no matching pull, and report one
   * that stays blank so the failure is counted. */
  useEffect(() => {
    if (state.phase !== "connected") return;
    const timer = window.setInterval(() => {
      const session = sessionRef.current;
      if (!session) return;
      for (const tile of Object.values(remoteTilesRef.current)) {
        const p = tile.participant;
        const missing = (["audio", "video", "screen"] as const).some((kind) => {
          const ref = p.tracks[kind];
          if (!ref) return false;
          const key = `${p.id}:${kind}`;
          if (pullingRef.current.has(key)) return false;
          const got = pulledRef.current.get(key);
          return !got || got.sessionId !== ref.sessionId || got.trackName !== ref.trackName;
        });
        if (!missing) continue;
        void syncParticipantTracksSafe(session, p).then(() => {
          const note = pullFailsRef.current.get(p.id);
          if (note && !note.reported && note.fails >= 3) {
            note.reported = true;
            socketRef.current?.sendLoose({
              type: "dropReport",
              outcome: "pull_failed",
              trigger: "media",
              awayMs: Date.now() - note.since,
              attempts: note.fails,
              hidden: document.visibilityState === "hidden",
              platform: "web",
            });
          }
        });
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [state.phase, syncParticipantTracksSafe]);

  /** When this tab's mic or camera went quiet (each is null while it is
   * fine), so a track the phone muted can be told from one it gave back. */
  const quietSinceRef = useRef<{ audio: number | null; video: number | null }>({ audio: null, video: null });
  const healingRef = useRef(false);
  /** Brings back a mic or camera the phone took away: a phone call answered
   * or dismissed mid-call ends or mutes both on a phone browser, and nothing
   * gives them back (QUEUE row 329; Adam, 2026-10-08: "he got a phone call
   * midway and it cut off the video/audio stream after he dismissed it and
   * he had to rejoin"). An ended track, or one still muted a few seconds
   * after the page is back in front, is opened again and put in the old
   * one's place on the same sender, so the others see and hear this person
   * again without anyone rejoining. `endedOnly` skips the muted wait (a
   * reconnect about to republish). Tries again on the next check while the
   * phone still holds the mic. */
  const healLocalMedia = useCallback(
    async (endedOnly = false): Promise<void> => {
      const camera = cameraRef.current;
      if (!camera || healingRef.current || flippingRef.current) return;
      const quiet = quietSinceRef.current;
      // A hidden phone page cannot open the camera, and a camera that is
      // muted while hidden is the browser's doing, not a loss.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        quiet.audio = null;
        quiet.video = null;
        return;
      }
      const now = Date.now();
      const lost = (track: MediaStreamTrack | null, kind: "audio" | "video"): boolean => {
        if (!track) return false;
        if (track.readyState === "ended") return true;
        if (!track.muted || endedOnly) {
          if (!track.muted) quiet[kind] = null;
          return false;
        }
        quiet[kind] ??= now;
        return now - quiet[kind]! >= QUIET_LOST_MS;
      };
      const audio = camera.audioTrack;
      // A closed camera (the off track stands in) is not a lost one.
      const video = offTrackRef.current ? null : blurRef.current ? blurRef.current.source : camera.videoTrack;
      const needAudio = lost(audio, "audio");
      const needVideo = lost(video, "video");
      if (!needAudio && !needVideo) return;
      healingRef.current = true;
      try {
        let fresh: CameraHandles;
        try {
          fresh = await openCamera({ audio: needAudio, video: needVideo, facingMode: facingRef.current });
        } catch {
          return;
        }
        if (cameraRef.current !== camera || mediaReleasedRef.current) {
          stopStream(fresh.stream);
          return;
        }
        if (needAudio && audio && fresh.audioTrack) {
          const next = fresh.audioTrack;
          next.enabled = audio.enabled;
          if (originalSoundOnRef.current) {
            await next.applyConstraints({ echoCancellation: false, noiseSuppression: false, autoGainControl: false }).catch(() => {});
          }
          sessionRef.current?.replaceTrack("audio", next);
          camera.stream.removeTrack(audio);
          audio.stop();
          camera.stream.addTrack(next);
          camera.audioTrack = next;
          quiet.audio = null;
        } else {
          fresh.audioTrack?.stop();
        }
        if (needVideo && video && fresh.videoTrack) {
          const next = fresh.videoTrack;
          next.enabled = video.enabled;
          const blur = blurRef.current;
          if (blur && blur.source === video) {
            blur.setSource(next);
          } else {
            sessionRef.current?.replaceTrack("video", next);
            camera.stream.removeTrack(video);
            camera.stream.addTrack(next);
            camera.videoTrack = next;
          }
          video.stop();
          quiet.video = null;
        } else {
          fresh.videoTrack?.stop();
        }
        setSelfStream(new MediaStream(camera.stream.getTracks()));
      } finally {
        healingRef.current = false;
      }
    },
    [],
  );

  /** Publishes whatever this client currently has to send — mic, camera (if
   * on), and screen share (if one was running) — in one batched negotiation,
   * and tells the room about each. Used both for the very first join and for
   * every reconnect: on a reconnect the OLD session and its transceivers are
   * gone, so this always starts from a clean slate rather than trying to
   * carry anything over from the dead RTCPeerConnection. */
  const republishLocalTracks = useCallback(async (session: CallSession, socket: RoomSocket): Promise<Set<MediaKind>> => {
    const sent = new Set<MediaKind>();
    // A reconnect after a phone call would otherwise publish the dead tracks.
    await healLocalMedia(true);
    const camera = cameraRef.current;
    if (!camera) return sent;
    const items: { readonly track: MediaStreamTrack; readonly kind: MediaKind; readonly originalSound?: boolean }[] = [];
    if (camera.audioTrack) items.push({ track: camera.audioTrack, kind: "audio", originalSound: originalSoundOnRef.current });
    // Published even while the camera is off (the track is disabled and
    // sends nothing), so turning it on is a switch the others see at once.
    if (camera.videoTrack) items.push({ track: camera.videoTrack, kind: "video" });
    if (items.length > 0) {
      // A guest let in after a wait at the door would push on a session the
      // SFU has already dropped.
      await session.refreshIfUnused(10_000);
      const published = await session.publishBatch(items);
      for (const p of published) {
        if (p.kind === "audio") midsRef.current.audio = p.mid;
        if (p.kind === "video") midsRef.current.video = p.mid;
        sent.add(p.kind);
        socket.send({
          type: "publishTrack",
          kind: p.kind,
          sessionId: session.id,
          trackName: p.trackName,
          ...(p.kind === "audio" ? { originalSound: originalSoundOnRef.current } : {}),
        });
      }
    }
    // The room starts everyone unmuted with the camera on; say otherwise.
    socket.send({
      type: "updateState",
      muted: !controlsRef.current.micOn || !camera.audioTrack,
      cameraOff: !controlsRef.current.cameraOn || !camera.videoTrack,
      away: document.visibilityState === "hidden",
    });
    // The screen capture itself survives a network drop (it is a local
    // getDisplayMedia() stream, unrelated to the SFU session) — only the
    // publish under it is gone, so republish it too if it was running.
    const screenTrack = screenStreamRef.current?.getVideoTracks()[0];
    if (screenTrack) {
      const published = await session.publish(screenTrack, "screen");
      midsRef.current.screen = published.mid;
      sent.add("screen");
      socket.send({ type: "publishTrack", kind: "screen", sessionId: session.id, trackName: published.trackName });
    }
    return sent;
  }, [healLocalMedia]);

  /** The call moved to another device of this account: this device lets go
   * of its camera, mic, and connection, and says where the call went. */
  const letGo = useCallback(() => {
    const socket = socketRef.current;
    socketRef.current = null;
    socket?.close();
    sessionRef.current?.close();
    releaseCamera();
    stopStream(screenStreamRef.current);
    screenStreamRef.current = null;
    setSelfStream(null);
    setScreenStream(null);
  }, []);

  const endMovedAway = useCallback(() => {
    letGo();
    dispatch({ type: "MOVED_AWAY" });
  }, [dispatch, letGo]);

  /** The owner finished the call for everyone (QUEUE row 264): the same
   * letting go, without waiting the ~18s the Room's close takes to arrive.
   * The socket is dropped first, so that late close is ignored. */
  const endFinished = useCallback(() => {
    letGo();
    dispatch({ type: "ROOM_CLOSED" });
  }, [dispatch, letGo]);

  /** This seat was taken by another window, or the month's cap refused the
   * call. The Room's 4001 close never reached a test socket in 60 seconds
   * (QUEUE row 266), so the message is the end: until this, the old window
   * kept its camera and its media session publishing. */
  const endLostSeat = useCallback(
    (message: string) => {
      letGo();
      dispatch({ type: "ERROR", message });
    },
    [dispatch, letGo],
  );

  const handleRoomMessage = useCallback(
    async (message: RoomMessage): Promise<void> => {
      const session = sessionRef.current;
      const socket = socketRef.current;
      if (!session || !socket) return;
      if (ingestPanel(message)) return;
      if (ingestChat(message)) return;
      if (ingestAssistant(message)) return;
      const loose = message as unknown as { type: string; byId?: unknown; by?: unknown; ok?: unknown; id?: unknown };
      if (loose.type === "unheard" && typeof loose.byId === "string") {
        if (loose.ok === true) unheardByRef.current.delete(loose.byId);
        else unheardByRef.current.set(loose.byId, typeof loose.by === "string" && loose.by ? loose.by : "Someone");
        return;
      }
      if (loose.type === "reveal" && typeof loose.id === "string") {
        reveal(loose.id);
        return;
      }
      if (loose.type === "audioDx" && typeof loose.id === "string") {
        // The room's reading of whose audio is the problem, for the host
        // (worker/src/audioPath.ts, row 506).
        const text = (loose as unknown as { text?: unknown }).text;
        if (typeof text === "string" && text) audioDxRef.current.set(loose.id, text);
        else audioDxRef.current.delete(loose.id);
        mediaIssueRef.current?.("");
        return;
      }
      if (loose.type === "mediaIssue" && typeof loose.id === "string") {
        mediaIssueRef.current?.(loose.id);
        return;
      }
      if (loose.type === "trackMissing") {
        // Someone cannot pull our mic or camera: the SFU took the push and
        // never registered the track (row 473). Push it again under a new
        // name and announce it; the room only sends this while the name is
        // still ours, so a second report for the old one is ignored there.
        const { kind, trackName } = loose as unknown as { kind?: unknown; trackName?: unknown };
        if ((kind !== "audio" && kind !== "video") || typeof trackName !== "string") return;
        const last = republishedAtRef.current[kind];
        if (last !== undefined && Date.now() - last < 8000) return;
        republishedAtRef.current[kind] = Date.now();
        const sound = kind === "audio" ? originalSoundOnRef.current : false;
        session
          .republish(kind, sound)
          .then((published) => {
            if (!published) return;
            midsRef.current[kind] = published.mid;
            socketRef.current?.send({
              type: "publishTrack",
              kind,
              sessionId: session.id,
              trackName: published.trackName,
              ...(kind === "audio" ? { originalSound: sound } : {}),
            });
          })
          .catch((err: unknown) => console.error(`Failed to push the ${kind} track again:`, err));
        return;
      }
      switch (message.type) {
        case "waiting":
          // Back from a drop, but the room no longer knew the seat.
          if (dropRef.current) {
            socket.sendLoose(dropReportOf(dropRef.current, "lobby"));
            noteRecent(recentRef.current, "back in the lobby");
            dropRef.current = null;
          }
          dispatch({ type: "KNOCK_SENT", knockId: message.knockId });
          // Still outside the call: the private goal typed here is kept, and
          // anything saved from before comes back.
          requestPanelPrivate();
          return;
        case "knockResult":
          dispatch(message.allowed ? { type: "KNOCK_APPROVED" } : { type: "KNOCK_DENIED" });
          return;
        case "welcome": {
          // The connection that brought this welcome reused the live media
          // session: what was pulled and what was published are still true.
          const kept = keptSessionRef.current;
          keptSessionRef.current = false;
          const sameSeat = kept && selfIdRef.current === message.selfId;
          if (dropRef.current) {
            socket.sendLoose(dropReportOf(dropRef.current, message.selfId === dropRef.current.selfId ? "restored" : "new_seat"));
            noteRecent(recentRef.current, message.selfId === dropRef.current.selfId ? "seat restored" : "new seat");
            dropRef.current = null;
          }
          markJoin("welcome");
          // A new seat starts every pair again; the same seat back from a
          // drop keeps who it already shows.
          if (!sameSeat) {
            for (const t of mediaReadyRef.current.values()) clearTimeout(t);
            mediaReadyRef.current.clear();
            revealedRef.current.clear();
            setRevealed(new Set());
          }
          socket.sendLoose({ type: "revealHello", fresh: !sameSeat });
          requestPanelPrivate();
          joinedAtRef.current ??= Date.now();
          setJoinedAt(joinedAtRef.current);
          setSelfId(message.selfId);
          selfIdRef.current = message.selfId;
          setRoom(message.room);
          setRecording(message.room.recording);
          setRecordingQuiet(!!message.room.recordingQuiet);
          setRecordArmed(!message.room.recording && !!message.room.recordArmed);
          recordingIdRef.current = message.room.recordingId ?? null;
          setRejoinToken(roomKey, message.rejoinToken);
          // A fresh session means a fresh RTCPeerConnection with no
          // transceivers at all — nothing pulled against the OLD session
          // means anything here, initial join or reconnect alike.
          if (!kept) {
            pulledRef.current.clear();
            pullingRef.current.clear();
            pullFailsRef.current.clear();
          }
          originalSoundRef.current.clear();
          handsRef.current.clear();
          setControls((prev) => ({ ...prev, handRaised: !!message.room.participants.find((p) => p.id === message.selfId)?.handRaisedAt }));
          setRemoteTiles((prev) => {
            const next: Record<string, RemoteTile> = {};
            for (const p of message.room.participants) {
              if (p.id === message.selfId) continue;
              const held = kept ? prev[p.id] : undefined;
              next[p.id] = held ? { ...held, participant: p } : { participant: p, audioTrack: null, videoTrack: null, screenTrack: null };
              originalSoundRef.current.set(p.id, p.originalSound);
              handsRef.current.set(p.id, p.handRaisedAt);
            }
            return next;
          });
          // Joining a call already in original sound: this mic joins in it too.
          if (!originalSoundOnRef.current && message.room.participants.some((p) => p.id !== message.selfId && p.originalSound)) {
            originalSoundOnRef.current = true;
            setControls((prev) => ({ ...prev, originalSoundOn: true }));
            socket.send({ type: "updateState", originalSound: true });
          }
          const selfSnapshot = message.room.participants.find((p) => p.id === message.selfId);
          // Same seat on the same media session: the room still lists every
          // track this client sends, so there is nothing to publish again and
          // nobody on the call sees a gap. Any other outcome publishes
          // afresh, after the old senders stop.
          const camera = cameraRef.current;
          const screenLive = !!screenStreamRef.current?.getVideoTracks()[0];
          const stillPublished =
            sameSeat &&
            !!selfSnapshot &&
            (!camera?.audioTrack || !!selfSnapshot.tracks.audio) &&
            (!camera?.videoTrack || !!selfSnapshot.tracks.video) &&
            (!screenLive || !!selfSnapshot.tracks.screen);
          let sent: Set<MediaKind>;
          if (stillPublished && selfSnapshot) {
            sent = new Set((["audio", "video", "screen"] as const).filter((k) => !!selfSnapshot.tracks[k]));
            socket.send({
              type: "updateState",
              muted: !controlsRef.current.micOn || !camera?.audioTrack,
              cameraOff: !controlsRef.current.cameraOn || !camera?.videoTrack,
              away: document.visibilityState === "hidden",
            });
          } else {
            try {
              if (kept) await session.stopSending();
              sent = await republishLocalTracks(session, socket);
              markJoin("published");
            } catch (err) {
              // Nothing went up, so the call screen would show and nobody
              // would see or hear this person, with no way back short of
              // leaving (QUEUE row 409). A fresh session and seat restore
              // instead.
              if (sessionRef.current !== session) return;
              noteRecent(recentRef.current, `publish failed: ${err instanceof Error ? err.message : String(err)}`.slice(0, 120));
              noteDrop("media");
              dispatch({ type: "NETWORK_LOST" });
              return;
            }
          }
          // Whatever stalls a first connection without an error (a fetch that
          // never answers) gets the same treatment: a session that has not
          // even started connecting 20 seconds after welcome is rebuilt.
          if (!kept) {
            const hasMedia = !!camera?.audioTrack || !!camera?.videoTrack;
            const others = message.room.participants.some((p) => p.id !== message.selfId);
            if (hasMedia || others) {
              setTimeout(() => {
                if (sessionRef.current !== session || session.pc.connectionState !== "new") return;
                noteRecent(recentRef.current, "media never started");
                noteDrop("media");
                dispatch({ type: "NETWORK_LOST" });
              }, 20_000);
            }
          }
          // A seat taken over from another device (or restored after a
          // drop) still lists what the old connection published. Whatever
          // this one does not send is withdrawn, so nobody keeps pulling a
          // track that ended with the other device.
          for (const k of ["audio", "video", "screen"] as const) {
            if (selfSnapshot?.tracks[k] && !sent.has(k)) socket.send({ type: "unpublishTrack", kind: k });
          }
          await Promise.all(
            message.room.participants
              .filter((p) => p.id !== message.selfId)
              .map((p) => syncParticipantTracksSafe(session, p)),
          );
          return;
        }
        case "knockWaiting":
          // A re-sent knock (each trivia answer re-sends it) keeps its place,
          // so two waiting cards do not swap on every answer (QUEUE row 423).
          setPendingKnocks((prev) =>
            prev.some((k) => k.knockId === message.knock.knockId)
              ? prev.map((k) => (k.knockId === message.knock.knockId ? message.knock : k))
              : [...prev, message.knock],
          );
          return;
        case "knockCancelled":
          setPendingKnocks((prev) => prev.filter((k) => k.knockId !== message.knockId));
          return;
        case "participantJoined":
          // The Room DO broadcasts these to the whole room, itself included
          // — "welcome" is the only message that already excludes self
          // (line above). Without this guard, a client would see its own
          // just-published tracks show up as a "participantJoined"/
          // "participantUpdated" about itself and pull them straight back
          // from the SFU: a real, growing inbound-rtp stream carrying its
          // own audio/video, discovered by the e2e negative control (a solo
          // participant should show zero inbound tracks, and didn't).
          if (message.participant.id === selfIdRef.current) return;
          originalSoundRef.current.set(message.participant.id, message.participant.originalSound);
          handsRef.current.set(message.participant.id, message.participant.handRaisedAt);
          upsertRemoteTile(message.participant);
          await syncParticipantTracksSafe(session, message.participant);
          return;
        case "participantUpdated": {
          if (message.participant.id === selfIdRef.current) {
            // Only the hand is read from our own update: the host may have
            // lowered it.
            const up = !!message.participant.handRaisedAt;
            setControls((prev) => (prev.handRaised === up ? prev : { ...prev, handRaised: up }));
            return;
          }
          const previousHand = handsRef.current.get(message.participant.id);
          handsRef.current.set(message.participant.id, message.participant.handRaisedAt);
          if (handWentUp({ handRaisedAt: previousHand }, message.participant)) {
            showNotice(`${message.participant.name} raised their hand`);
          }
          const previousOriginalSound = originalSoundRef.current.get(message.participant.id);
          originalSoundRef.current.set(message.participant.id, message.participant.originalSound);
          // An original-sound toggle now arrives as a republished audio track
          // carrying the new flag, which syncParticipantTracks pulls fresh
          // with the right stereo declaration; re-pulling it again below
          // would only do the same work twice.
          const pulledAudio = pulledRef.current.get(`${message.participant.id}:audio`);
          const nextAudio = message.participant.tracks.audio;
          const audioRepublished =
            !!pulledAudio &&
            !!nextAudio &&
            (pulledAudio.sessionId !== nextAudio.sessionId || pulledAudio.trackName !== nextAudio.trackName);
          upsertRemoteTile(message.participant);
          await syncParticipantTracksSafe(session, message.participant);
          // A toggle of original sound on a participant whose audio we've
          // ALREADY pulled never changes their (sessionId, trackName), so
          // syncParticipantTracks's diff above is a no-op for it — the mid
          // itself has to be re-negotiated directly instead of re-pulled.
          if (
            previousOriginalSound !== undefined &&
            previousOriginalSound !== message.participant.originalSound &&
            !audioRepublished &&
            pulledRef.current.has(`${message.participant.id}:audio`)
          ) {
            void session.setStereoForParticipant(message.participant.id, message.participant.originalSound);
          }
          if (previousOriginalSound !== undefined && previousOriginalSound !== message.participant.originalSound) {
            followOriginalSoundRef.current(message.participant.originalSound);
          }
          return;
        }
        case "participantLeft":
          // Stop receiving from this participant on the SFU side too, or a
          // long call accumulates dead recvonly transceivers for every
          // participant who's ever come and gone. Best-effort: never let a
          // failure here block removing their tile from the UI below.
          void session.closeReceivedTracksFor(message.participantId).catch((err: unknown) => {
            console.error(`Failed to close received tracks for ${message.participantId}:`, err);
          });
          clearParticipantPulls(message.participantId);
          originalSoundRef.current.delete(message.participantId);
          handsRef.current.delete(message.participantId);
          setRemoteTiles((prev) => {
            const next = { ...prev };
            delete next[message.participantId];
            return next;
          });
          return;
        case "recordingState":
          // A quiet one reaches the host alone and does not light the
          // indicator; tapping Record later sends the same state without
          // the flag.
          setRecording(message.recording && !message.quiet);
          setRecordingQuiet(!!message.quiet && message.recording);
          setRecordArmed(false);
          recordingIdRef.current = message.recordingId ?? recordingIdRef.current;
          return;
        case "recordingDiscarded":
          setRecordingSaved((cur) => (cur && cur.recordingId === message.recordingId ? { ...cur, phase: "deleted" } : cur));
          savedHeldRef.current = false;
          closeSavedIn(DELETED_POPUP_MS);
          return;
        case "movedAway":
          endMovedAway();
          return;
        case "ended":
          endFinished();
          return;
        case "roomFull":
          dispatch({ type: "ERROR", message: `This room is full (max ${message.maxParticipants}).` });
          return;
        case "error":
          // Only losing the seat ends the call. The rest (a recording that
          // failed to start, a malformed message) leave the call running;
          // a failed recording leaves Record off, which is the truth.
          if (message.code === "replaced" || message.code === "cap_reached") {
            endLostSeat(message.message);
          } else if (message.code === "recording_failed") {
            setRecording(false);
            recordingIdRef.current = null;
            showNotice(message.message);
          } else {
            console.error(`Room error ${message.code}: ${message.message}`);
          }
          return;
        case "pong":
          return;
      }
    },
    [clearParticipantPulls, closeSavedIn, dispatch, endFinished, endLostSeat, endMovedAway, ingestAssistant, ingestChat, ingestPanel, noteDrop, requestPanelPrivate, republishLocalTracks, reveal, roomKey, showNotice, syncParticipantTracksSafe, upsertRemoteTile],
  );

  /** Creates a fresh CallSession + RoomSocket and connects. Used for the
   * initial join AND for every reconnect — a reconnect is nothing more than
   * running this again after tearing down whatever session/socket is left
   * from the drop, since `handleRoomMessage`'s "welcome" case already
   * republishes local tracks and re-pulls the room's current participants
   * off whatever is current in `sessionRef`/`socketRef` at the time it runs,
   * never off a value captured by closure at an earlier connection attempt. */
  const connectOnce = useCallback(
    async (cancelledRef: { current: boolean }, keepSession = false): Promise<void> => {
      // A lost socket with the media still up reopens only the socket: the
      // session, the tracks it sends and the ones it pulls all carry on, so
      // nobody hears a gap. The welcome that follows reads this.
      const live = sessionRef.current;
      if (keepSession && live && live.mediaUp) {
        keptSessionRef.current = true;
      } else {
        keptSessionRef.current = false;
        await buildSession();
        if (cancelledRef.current) return;
      }

      async function buildSession(): Promise<void> {
        const iceServers = await fetchIceServers();
        markJoin("ice");
        let mediaLossTimer: ReturnType<typeof setTimeout> | null = null;
        const session = new CallSession(
          {
            onRemoteTrack: (participantId, handle) => {
              if (isDebug) {
                const w = window as unknown as { __breezeTrackLog?: unknown[] };
                w.__breezeTrackLog ??= [];
                w.__breezeTrackLog.push({ participantId, kind: handle.kind, t: Date.now() });
              }
              setRemoteTiles((prev) => {
                const existing = prev[participantId];
                if (isDebug && !existing) {
                  const w = window as unknown as { __breezeDroppedTracks?: unknown[] };
                  w.__breezeDroppedTracks ??= [];
                  w.__breezeDroppedTracks.push({ participantId, kind: handle.kind, knownIds: Object.keys(prev), t: Date.now() });
                }
                if (!existing) return prev;
                const next: RemoteTile = { ...existing };
                if (handle.kind === "audio") (next as { audioTrack: MediaStreamTrack | null }).audioTrack = handle.track;
                if (handle.kind === "video") (next as { videoTrack: MediaStreamTrack | null }).videoTrack = handle.track;
                if (handle.kind === "screen") (next as { screenTrack: MediaStreamTrack | null }).screenTrack = handle.track;
                return { ...prev, [participantId]: next };
              });
            },
            onConnectionStateChange: (pcState) => {
              noteRecent(recentRef.current, `media ${pcState}`);
              if (mediaLossTimer) {
                clearTimeout(mediaLossTimer);
                mediaLossTimer = null;
              }
              if (pcState === "connected") {
                markJoin("media");
                sendJoinTiming(false);
                dispatch({ type: "SFU_CONNECTED" });
              }
              // "disconnected" often comes back by itself within a few seconds
              // (a network handoff, a short stall), and treating it as lost at
              // once turned every blip into a full reconnect. It counts as lost
              // once it has lasted 3 seconds, as it does on iOS.
              if (pcState === "disconnected") {
                mediaLossTimer = setTimeout(() => {
                  mediaLossTimer = null;
                  if (sessionRef.current !== session) return;
                  noteDrop("media");
                  dispatch({ type: "NETWORK_LOST" });
                }, 3000);
              }
              if (pcState === "failed") {
                noteDrop("media");
                dispatch({ type: "NETWORK_LOST" });
              }
            },
          },
          iceServers,
        );
        sessionRef.current = session;
        // Debug-only escape hatch for the e2e harness (scripts/e2e-call.mjs):
        // it needs the real RTCPeerConnection to read inbound-rtp stats and
        // confirm bytes are actually flowing, not just that the UI rendered.
        // Gated on an explicit query param so this never reaches a normal call.
        if (
          typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("debug") === "1"
        ) {
          (window as unknown as { __breezePc?: RTCPeerConnection }).__breezePc = session.pc;
          (window as unknown as { __breezeSession?: CallSession }).__breezeSession = session;
        }
        await session.start();
        markJoin("session");
      }

      const name = displayName.trim() || "Guest";
      const deviceId = getOrCreateDeviceId();
      // Present only on a reconnect — a fresh first join has never stored
      // one. Putting it straight on the connect URL is what lets
      // worker/src/room.ts's acceptCall restore the OLD participant before
      // it would otherwise create (and broadcast) a brand-new throwaway
      // one; see that function's own comment for why the ordering matters.
      const existingRejoin = getRejoinToken(roomKey);
      const url = roomWsUrl(
        kind,
        slug,
        name,
        deviceId,
        existingRejoin ?? undefined,
        getUserKey() ?? undefined,
        options.move,
        options.record ? { record: "1" } : undefined,
      );
      const socket = new RoomSocket(url, {
        // A socket this client has since replaced (a reconnect) can still
        // deliver a last message or two, including the room's `replaced`
        // notice meant for it; none of that is about the current call.
        onMessage: (message) => {
          if (socketRef.current !== socket) return;
          void handleRoomMessage(message);
        },
        onOpen: () => {
          markJoin("socket");
          // Belt-and-suspenders fallback for acceptCall's own rejoinToken
          // handling above: if the URL's token had already expired or been
          // claimed by an earlier attempt, this WS message gives the room
          // one more chance to restore the old identity. In the normal case
          // acceptCall already consumed the token, so handleMessage's own
          // "join" case finds nothing left to claim and no-ops.
          if (existingRejoin) socket.send({ type: "join", deviceId, name, rejoinToken: existingRejoin });
        },
        onClose: (event) => {
          // 4000 is worker/src/room.ts's EXPLICIT_LEAVE_CODE — this client
          // asked to leave, so there is nothing to reconnect (BRIEF 7.2:
          // leave never shows a reconnecting state on the leaver's own
          // screen). Any other code is an unexpected drop.
          if (cancelledRef.current || event.code === 4000) return;
          // A socket this client already replaced (a reconnect) closing late
          // says nothing about the current connection.
          if (socketRef.current !== socket) return;
          // 4001: the room gave this seat to a newer connection from the
          // same device (another tab, or this page reopened elsewhere).
          // Reconnecting would take it back and start a tug of war.
          // 4003: this account moved the call to another device.
          if (event.code === ROOM_CLOSE_CODES.movedAway) {
            endMovedAway();
            return;
          }
          // 4005: the call was ended for everyone (an Auto Breeze call hung up).
          if (event.code === ROOM_CLOSE_CODES.ended) {
            dispatch({ type: "ROOM_CLOSED" });
            return;
          }
          if (event.code === 4001) {
            dispatch({ type: "ERROR", message: "You joined this call from another window." });
            return;
          }
          // 4002: the month's usage cap refused a new call (BRIEF 13).
          if (event.code === 4002) {
            dispatch({ type: "ERROR", message: "Calls are paused until next month. This month's free video is used up." });
            return;
          }
          noteDrop("socket", event.code);
          dispatch({ type: "NETWORK_LOST" });
        },
      });
      socketRef.current = socket;
      socket.setPingMs(assistantOnRef.current ? PING_ASSISTANT_MS : PING_MS);
      socket.connect();
    },
    [dispatch, displayName, endMovedAway, handleRoomMessage, kind, noteDrop, options.move, options.record, roomKey, slug],
  );

  useEffect(() => {
    const cancelledRef = { current: false };

    async function join(): Promise<void> {
      // The state machine's "idle" phase only accepts PREVIEW_READY (see
      // packages/core/src/stateMachine.ts) — this screen has no live preview
      // step of its own (PreJoinScreen never calls getUserMedia; by the time
      // CallScreen mounts, the user has already committed to joining), so
      // both transitions fire back to back rather than skipping straight to
      // JOIN_REQUESTED. Without PREVIEW_READY first, JOIN_REQUESTED from
      // "idle" is a no-op per the state machine's own rules (an event that
      // doesn't apply to the current phase does nothing), phase never leaves
      // "idle", and every later SFU_CONNECTED is silently swallowed too —
      // found via the e2e harness: real media was flowing (bytesReceived
      // growing, remote tracks arriving) while the screen sat on
      // "Connecting…" forever.
      dispatch({ type: "PREVIEW_READY" });
      dispatch({ type: "JOIN_REQUESTED" });
      joinMarksRef.current = { t0: Date.now(), marks: {}, slowSent: false, sent: false };
      setTimeout(() => {
        if (!cancelledRef.current && !("media" in (joinMarksRef.current?.marks ?? {}))) sendJoinTiming(true);
      }, JOIN_SLOW_MS);
      try {
        // Never throws: without a camera or mic the call still joins, to
        // see and hear the others, and says why.
        const media = await openCallMedia();
        markJoin("camera");
        if (cancelledRef.current) {
          stopStream(media.stream);
          return;
        }
        if (media.audioTrack) media.audioTrack.enabled = getMicEnabled();
        let startVideo = media.videoTrack;
        if (startVideo && !getCameraEnabled()) {
          // Joining with the camera off: close it now rather than leave the
          // light on behind a disabled track.
          const off = createOffTrack();
          media.stream.removeTrack(startVideo);
          startVideo.stop();
          media.stream.addTrack(off.track);
          offTrackRef.current = off;
          startVideo = off.track;
        }
        const camera = { stream: media.stream, audioTrack: media.audioTrack, videoTrack: startVideo };
        cameraRef.current = camera;
        mediaReleasedRef.current = false;
        setSelfStream(camera.stream);
        setControls((prev) => ({
          ...prev,
          micOn: prev.micOn && !!media.audioTrack,
          cameraOn: prev.cameraOn && !!media.videoTrack,
        }));
        const problem = mediaProblemText(media);
        if (problem) showNotice(problem);
        void hasMultipleCameras().then((can) => {
          if (!cancelledRef.current) setControls((prev) => ({ ...prev, canFlipCamera: can }));
        });
        await connectOnce(cancelledRef);
        if (!cancelledRef.current && controlsRef.current.blurOn) {
          void setBlur(true).then((ok) => {
            if (!ok) setControls((prev) => ({ ...prev, blurOn: false }));
          });
        }
      } catch (err) {
        if (!cancelledRef.current) {
          dispatch({ type: "ERROR", message: err instanceof Error ? err.message : "Could not join the call." });
        }
      }
    }

    void join();

    return () => {
      cancelledRef.current = true;
      socketRef.current?.close();
      sessionRef.current?.close();
      releaseCamera();
      stopStream(screenStreamRef.current);
    };
    // Intentionally run once per mount — a change of room/name mid-call is a
    // fresh call, not a resync, and the screen that owns this hook only
    // mounts once per call attempt. `connectOnce` closes over `dispatch` and
    // the stable-for-the-life-of-this-call kind/slug/displayName/roomKey, so
    // it is safe to omit here for the same reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** True when the drop being recovered was the socket alone, the media
   * session is still up, and this is among the first tries. Anything else
   * (the media dropped, the network is gone, several tries have failed)
   * rebuilds the session from scratch. */
  const canKeepMedia = useCallback((): boolean => {
    const note = dropRef.current;
    const session = sessionRef.current;
    return !!note && !!session && note.trigger === "socket" && note.attempts <= 2 && session.mediaUp;
  }, []);

  // Drives the actual reconnect: runs whenever the state machine is in
  // "reconnecting", tears down whatever is left of the dropped
  // session/socket, and calls connectOnce() again. Backed off by attempt
  // number (packages/core's transition() increments `attempt` on every
  // RECONNECT_FAILED and gives up after MAX_RECONNECT_ATTEMPTS), and the
  // effect's own cleanup cancels a pending retry the moment the phase
  // changes away from "reconnecting" — on success (NETWORK_RESTORED) or on
  // giving up (the state machine moves to "ended").
  useEffect(() => {
    if (state.phase !== "reconnecting") return;

    let cancelled = false;
    const delayMs = Math.min(1000 * 2 ** (state.attempt - 1), 8000);

    async function attempt(): Promise<void> {
      if (cancelled || reconnectInFlightRef.current) return;
      // A phone in a phone call cannot rejoin, and every try spent while it
      // is in one counts toward giving up, which ended the call for a guest
      // whose call ran past the tries (QUEUE row 329). The page coming back
      // in front tries at once (the effect below). A computer keeps trying
      // with the tab behind another one.
      if (document.visibilityState === "hidden" && onPhone()) return;
      reconnectInFlightRef.current = true;
      if (dropRef.current) dropRef.current.attempts += 1;
      try {
        const keep = canKeepMedia();
        socketRef.current?.close();
        if (!keep) sessionRef.current?.close();
        const cancelledRef = { current: false };
        await connectOnce(cancelledRef, keep);
        if (!cancelled) dispatch({ type: "NETWORK_RESTORED" });
      } catch {
        if (!cancelled) dispatch({ type: "RECONNECT_FAILED" });
      } finally {
        reconnectInFlightRef.current = false;
      }
    }

    const timer = setTimeout(() => void attempt(), delayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [state, canKeepMedia, connectOnce, dispatch]);

  // A browser "online" event is a much stronger, much faster signal than the
  // backoff timer above that the network is actually back — nudge a retry
  // immediately rather than waiting out whatever backoff is left. Harmless
  // if it races the timer-driven attempt: reconnectInFlightRef makes the
  // second caller a no-op.
  useEffect(() => {
    if (state.phase !== "reconnecting") return;
    const onOnline = (): void => {
      void (async () => {
        if (reconnectInFlightRef.current) return;
        reconnectInFlightRef.current = true;
        if (dropRef.current) dropRef.current.attempts += 1;
        try {
          const keep = canKeepMedia();
          socketRef.current?.close();
          if (!keep) sessionRef.current?.close();
          const cancelledRef = { current: false };
          await connectOnce(cancelledRef, keep);
          dispatch({ type: "NETWORK_RESTORED" });
        } catch {
          dispatch({ type: "RECONNECT_FAILED" });
        } finally {
          reconnectInFlightRef.current = false;
        }
      })();
    };
    // A phone that brings this tab back from the background is the same
    // signal: its timers were frozen while hidden, so try now.
    const onVisible = (): void => {
      if (document.visibilityState === "visible") onOnline();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [state.phase, canKeepMedia, connectOnce, dispatch]);

  // What each arriving camera looks like, sampled every 15 seconds for the
  // leaving report, which has to be built synchronously at Leave.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const session = sessionRef.current;
      if (!session || !selfIdRef.current) return;
      void session
        .inboundVideo()
        .then((rows) => {
          videoInRef.current = rows
            .map((r) => `${r.rid ?? "?"} ${r.width ?? 0}x${r.height ?? 0} ${Math.round(r.fps ?? 0)}fps`)
            .join("; ");
        })
        .catch(() => {});
    }, 15000);
    return () => window.clearInterval(timer);
  }, []);

  // What this side hears and sees of each other person, and what it sends,
  // to the room every ten seconds (lib/mediaHealth.ts). The room logs when
  // someone stops hearing or seeing someone, and `room-status.mjs --media`
  // shows it live.
  //
  // The same counters are read every two seconds to catch a problem while
  // the person is still there (Adam, 2026-10-16: "what if they recognize the
  // problem and leave or something in under 10s?"). Four seconds of nothing
  // from someone who is sending pulls their audio again, which is what a
  // leave and rejoin did (QUEUE row 413), and the screen says so on both
  // sides: "Can't hear Shubh" here, "Adam can't hear you" there. Six more
  // seconds without sound asks for a tap, and so does a browser that will
  // not play the sound until one. A camera with no frames for six seconds
  // is pulled again quietly, since a frozen picture already shows.
  useEffect(() => {
    if (state.phase !== "connected") return;
    let prev: MediaCounters | null = null;
    let last: MediaCounters | null = null;
    let tick = 0;
    const emptyVideo = new Map<string, number>();
    // Ticks someone has published unmuted audio with no track on their tile.
    // A track that never arrived used to read as "cannot tell" forever, so
    // nobody was told they could not be heard (QUEUE row 473).
    const noAudio = new Map<string, number>();
    const lastRePull = new Map<string, { audio: number; video: number }>();
    const micEnergy: (number | null)[] = [];
    let micSilent = false;
    let repulled: { id: string; kind: string }[] = [];
    let busy = false;
    const hearing = hearingRef.current;
    const unheardBy = unheardByRef.current;
    hearing.clear();
    unheardBy.clear();
    audioDxRef.current.clear();

    const rePull = (id: string, kind: "audio" | "video"): void => {
      const session = sessionRef.current;
      if (!session) return;
      lastRePull.set(id, { ...(lastRePull.get(id) ?? { audio: 0, video: 0 }), [kind]: Date.now() });
      noteRecent(recentRef.current, `repull ${kind}`);
      repulled.push({ id, kind });
      void session.rePull(id, kind).catch((err: unknown) => console.warn("[breeze] re-pull failed", err));
    };
    const playAll = (): void => {
      document.querySelectorAll("audio").forEach((el) => {
        if (el.srcObject && el.paused) void el.play().catch(() => {});
      });
    };
    // What the last few ticks counted, sent with a "can't hear" so the room's
    // log can tell a lost call from a false alarm (QUEUE row 475: the guest on
    // the 2026-10-28 call was told "Adam can't hear you" and the row had no
    // numbers at all).
    const trail = new Map<string, { pk: number[]; fr: number[] }>();
    const gaps: number[] = [];
    let rxDelta = 0;
    let lastState: { ice?: string; conn?: string } = {};
    // Ids whose loss the room called from both ends' reports (silence or
    // nothing playing). This page's own count sees packets arrive within
    // four seconds of the re-pull and would say "back" to the speaker while
    // the sound was still gone, so the room alone tells them about those.
    const roomHeld = new Set<string>();
    const tellThem = (id: string, ok: boolean): void => {
      const t = trail.get(id);
      socketRef.current?.sendLoose({
        type: "hearing",
        id,
        ok,
        ...(ok
          ? {}
          : {
              d: {
                pk: t?.pk ?? [],
                fr: t?.fr ?? [],
                gaps,
                rx: rxDelta,
                ...lastState,
                hidden: document.visibilityState === "hidden",
              },
            }),
      });
    };
    const setWatch = (id: string, next: HearWatch): void => {
      const was = hearing.get(id)?.state ?? "ok";
      // Kept while any count is running: an "ok" watch one empty tick into a
      // loss has to carry that tick to the next one.
      if (next.state === "ok" && !next.empty && !next.unplayed) hearing.delete(id);
      else hearing.set(id, next);
      if ((was === "ok") !== (next.state === "ok")) {
        if (!roomHeld.has(id)) tellThem(id, next.state === "ok");
        if (next.state === "ok") roomHeld.delete(id);
      }
    };
    const nameOf = (id: string): string => remoteTilesRef.current[id]?.participant.name || "them";
    const refreshAlert = (): void => {
      const pick = (state: HearWatch["state"]): string | undefined =>
        [...hearing.entries()].find(([id, w]) => w.state === state && remoteTilesRef.current[id])?.[0];
      let next: MediaAlert | null = null;
      const tap = pick("tap");
      const stuck = pick("stuck");
      const reconnecting = pick("reconnecting");
      if (tap) {
        next = { text: `Your browser is holding back ${nameOf(tap)}'s sound.`, action: { label: "Play sound", run: playAll } };
      } else if (stuck) {
        next = {
          text: `Still can't hear ${nameOf(stuck)}.`,
          action: {
            label: "Try again",
            run: () => {
              setWatch(stuck, { ...HEAR_OK, state: "reconnecting" });
              rePull(stuck, "audio");
              refreshAlert();
            },
          },
        };
      } else if (reconnecting) {
        next = { text: `Can't hear ${nameOf(reconnecting)}. Reconnecting their sound.` };
      } else {
        for (const id of [...unheardBy.keys()]) if (!remoteTilesRef.current[id]) unheardBy.delete(id);
        const first = [...unheardBy.values()][0];
        const dx = [...audioDxRef.current.entries()].find(([id]) => !!remoteTilesRef.current[id])?.[1];
        if (first) next = { text: `${first} can't hear you. Reconnecting.` };
        else if (dx) next = { text: dx };
        else if (micSilent) next = { text: "Your microphone is sending no sound. Check it isn't muted on the device." };
      }
      setMediaAlert((cur) => (cur?.text === next?.text ? cur : next));
    };
    // The room's own verdict, from both sides' reports: packets arrive but
    // carry silence, or nothing plays them. Pull their audio again.
    mediaIssueRef.current = (id: string) => {
      // An empty id is the host's reading changing: only the line is redrawn.
      if (id === "") {
        refreshAlert();
        return;
      }
      if (!remoteTilesRef.current[id]) return;
      if ((hearing.get(id)?.state ?? "ok") === "ok") {
        roomHeld.add(id);
        setWatch(id, { ...HEAR_OK, state: "reconnecting" });
        rePull(id, "audio");
        refreshAlert();
      }
    };

    const timer = window.setInterval(() => {
      const session = sessionRef.current;
      if (!session || !selfIdRef.current || busy) return;
      busy = true;
      void session
        .mediaCounters()
        .then((cur) => {
          const tiles = Object.values(remoteTilesRef.current);
          const playing = new Set<string>();
          document.querySelectorAll("audio").forEach((el) => {
            const track = (el.srcObject as MediaStream | null)?.getAudioTracks?.()[0];
            if (track && !el.paused) playing.add(track.id);
          });
          const now = Date.now();

          // The two-second check.
          const before = new Map((last?.inbound ?? []).map((c) => [c.mid, c] as const));
          const byTrack = new Map(cur.inbound.map((c) => [c.trackId, c] as const));
          // A sample taken right after the last one (a timer that was held up
          // and then caught up) spans too little time for "no packets" to
          // mean anything, so it counts as "cannot tell".
          const gapMs = last ? cur.at - last.at : null;
          const tooSoon = gapMs !== null && gapMs < 1200;
          if (gapMs !== null) {
            gaps.push(Math.round(gapMs));
            if (gaps.length > 4) gaps.shift();
          }
          const sumBytes = (c: MediaCounters | null): number => (c ? c.inbound.reduce((n, i) => n + i.bytes, 0) : 0);
          rxDelta = last ? Math.max(0, sumBytes(cur) - sumBytes(last)) : 0;
          lastState = { ...(cur.ice ? { ice: cur.ice } : {}), ...(cur.conn ? { conn: cur.conn } : {}) };
          const count = (trackId: string | undefined, field: "packets" | "frames"): number => {
            if (tooSoon) return -1;
            const c = trackId ? byTrack.get(trackId) : undefined;
            if (!c) return trackId ? 0 : -1;
            const was = before.get(c.mid);
            return was ? Math.max(0, c[field] - was[field]) : -1;
          };
          for (const id of [...hearing.keys()]) if (!remoteTilesRef.current[id]) hearing.delete(id);
          for (const id of [...noAudio.keys()]) if (!remoteTilesRef.current[id]) noAudio.delete(id);
          const seen: Record<string, unknown>[] = [];
          for (const tile of tiles) {
            const p = tile.participant;
            if (p.phoneCall) continue;
            const audioId = tile.audioTrack?.id;
            const missing = !audioId && !!p.tracks.audio && !p.muted ? (noAudio.get(p.id) ?? 0) + 1 : 0;
            noAudio.set(p.id, missing);
            // Ten seconds covers a first pull and all its retries.
            const packets = missing >= 5 ? 0 : last ? count(audioId, "packets") : -1;
            const frames = last ? count(tile.videoTrack?.id, "frames") : -1;
            const tr = trail.get(p.id) ?? { pk: [], fr: [] };
            tr.pk.push(packets);
            tr.fr.push(frames);
            if (tr.pk.length > 4) tr.pk.shift();
            if (tr.fr.length > 4) tr.fr.shift();
            trail.set(p.id, tr);
            const { next, act } = hearStep(hearing.get(p.id) ?? HEAR_OK, {
              packets,
              sending: !!p.tracks.audio && !p.muted,
              playing: !!audioId && playing.has(audioId),
            });
            setWatch(p.id, next);
            seen.push({ id: p.id, audioId: audioId ?? null, packets, state: next.state, act });
            const cool = lastRePull.get(p.id) ?? { audio: 0, video: 0 };
            if (act === "repull") rePull(p.id, "audio");
            else if (act === "play") playAll();
            // Stuck: keep trying every fifteen seconds as well as on a tap.
            else if (next.state === "stuck" && now - cool.audio > 15_000) rePull(p.id, "audio");
            const camOn = !!p.tracks.video && !p.cameraOff && !p.away;
            const empty = camOn && frames === 0 ? (emptyVideo.get(p.id) ?? 0) + 1 : 0;
            emptyVideo.set(p.id, empty);
            if (empty >= 3 && now - cool.video > 15_000) {
              emptyVideo.set(p.id, 0);
              rePull(p.id, "video");
            }
          }
          const o = cur.out;
          const lo = last?.out;
          micEnergy.push(
            controlsRef.current.micOn && lo && o.micEnergy !== null && lo.micEnergy !== null && o.micSecs !== null && lo.micSecs !== null && o.micSecs > lo.micSecs
              ? o.micEnergy - lo.micEnergy
              : null,
          );
          if (micEnergy.length > 6) micEnergy.shift();
          micSilent = micDead(micEnergy);
          last = cur;
          refreshAlert();
          // For the end-to-end test: what the check saw on its last tick.
          (window as unknown as { __breezeHear?: unknown }).__breezeHear = { at: now, peers: seen, micSilent };

          // The ten-second report: every fifth tick, after a first sample.
          if (prev && ++tick % 5 !== 0) return;
          const report = healthReport({
            prev,
            cur,
            peers: tiles.map((t) => ({
              id: t.participant.id,
              audioTrackId: t.audioTrack?.id ?? null,
              videoTrackId: t.videoTrack?.id ?? null,
              screenTrackId: t.screenTrack?.id ?? null,
            })),
            playing,
            micOn: controlsRef.current.micOn,
            cameraOn: controlsRef.current.cameraOn,
            sharing: !!screenStreamRef.current?.getVideoTracks()[0],
            hidden: document.visibilityState === "hidden",
            micDead: micSilent,
            micTrack: (() => {
              const t = cameraRef.current?.audioTrack;
              return t ? { en: t.enabled, mu: t.muted, rs: t.readyState } : null;
            })(),
            net: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType ?? null,
          });
          const hadPrev = prev !== null;
          prev = cur;
          if (!hadPrev) return;
          socketRef.current?.sendLoose({ type: "mediaHealth", ...report, ...(repulled.length ? { repulled } : {}) });
          repulled = [];
        })
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    }, 2_000);
    return () => {
      window.clearInterval(timer);
      mediaIssueRef.current = null;
      hearing.clear();
      unheardBy.clear();
      setMediaAlert(null);
    };
  }, [state.phase]);

  // The network and the tab's visibility, for the leaving report.
  useEffect(() => {
    const note = (e: string) => () => noteRecent(recentRef.current, e);
    const onOffline = note("offline");
    const onOnline = note("online");
    const onVisibility = (): void => noteRecent(recentRef.current, document.visibilityState === "hidden" ? "tab hidden" : "tab shown");
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // The others see this tab put away (a phone's browser in the background
  // stops the camera, so the tile says they are still there instead of
  // going black).
  useEffect(() => {
    const onVisibility = (): void => {
      if (selfIdRef.current) socketRef.current?.send({ type: "updateState", away: document.visibilityState === "hidden" });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // This tab's own mic and camera, checked every couple of seconds while the
  // call is on and at once when the page comes back in front, so a phone
  // call that took them is undone without a rejoin (QUEUE row 329).
  useEffect(() => {
    if (state.phase !== "connected" && state.phase !== "reconnecting" && state.phase !== "waitingForApproval") return;
    const check = (): void => void healLocalMedia();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") check();
    };
    const timer = setInterval(check, MEDIA_CHECK_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", check);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("focus", check);
    };
  }, [state.phase, healLocalMedia]);

  // A reconnect that gave up has no socket to report on, so it says so by
  // beacon: one request, and only then.
  useEffect(() => {
    if (state.phase !== "ended" || state.reason !== "roomClosed") return;
    const note = dropRef.current;
    if (!note) return;
    dropRef.current = null;
    const body = JSON.stringify({
      ...dropReportOf(note, "gave_up"),
      rejoinToken: getRejoinToken(roomKey) ?? undefined,
      name: displayName.trim() || "Guest",
    });
    try {
      navigator.sendBeacon?.(`/api/rooms/${kind}/${encodeURIComponent(slug)}/drop`, new Blob([body], { type: "application/json" }));
    } catch {
      // The log misses one row; the call is already over.
    }
  }, [state, kind, slug, roomKey, displayName]);

  /** Opens a camera or mic the call started without (blocked, then
   * allowed; or plugged in since) and publishes it. The tap is the user
   * gesture that lets the browser ask again. */
  const addMissingTrack = useCallback(
    async (kind: "audio" | "video"): Promise<boolean> => {
      const camera = cameraRef.current;
      const session = sessionRef.current;
      if (!camera) return false;
      let handles;
      try {
        handles = await openCamera({ video: kind === "video", audio: kind === "audio", facingMode: facingRef.current });
      } catch (err) {
        const denied = (err as { name?: string } | null)?.name === "NotAllowedError";
        showNotice(
          kind === "video"
            ? denied
              ? "Your camera is blocked. Allow it for this site in your browser's settings."
              : "No camera found."
            : denied
              ? "Your mic is blocked. Allow it for this site in your browser's settings."
              : "No mic found.",
        );
        return false;
      }
      const track = kind === "video" ? handles.videoTrack : handles.audioTrack;
      if (!track) return false;
      camera.stream.addTrack(track);
      if (kind === "video") camera.videoTrack = track;
      else camera.audioTrack = track;
      setSelfStream(new MediaStream(camera.stream.getTracks()));
      // Before the room lets this person in, the welcome's republish
      // carries the new track; publishing now would publish it twice.
      if (session && selfIdRef.current) {
        const [published] = await session.publishBatch([
          kind === "audio" ? { track, kind, originalSound: originalSoundOnRef.current } : { track, kind },
        ]);
        if (published) {
          midsRef.current[kind] = published.mid;
          socketRef.current?.send({
            type: "publishTrack",
            kind,
            sessionId: session.id,
            trackName: published.trackName,
            ...(kind === "audio" ? { originalSound: originalSoundOnRef.current } : {}),
          });
        }
      }
      return true;
    },
    [showNotice],
  );

  const toggleMic = useCallback(() => {
    const next = !controlsRef.current.micOn;
    const track = cameraRef.current?.audioTrack;
    if (next && !track) {
      void addMissingTrack("audio").then((ok) => {
        if (!ok) return;
        setControls((prev) => ({ ...prev, micOn: true }));
        if (selfIdRef.current) socketRef.current?.send({ type: "updateState", muted: false });
      });
      return;
    }
    if (track) track.enabled = next;
    // A guest still waiting to be let in is not a participant yet, and the
    // room would announce the update to everyone in it.
    if (selfIdRef.current) socketRef.current?.send({ type: "updateState", muted: !next });
    setControls((prev) => ({ ...prev, micOn: next }));
  }, [addMissingTrack]);

  /** Puts `to` in `from`'s place everywhere the camera's video is held. */
  const swapVideo = useCallback((camera: MutableCameraHandles, from: MediaStreamTrack, to: MediaStreamTrack) => {
    sessionRef.current?.replaceVideoTrack(to);
    camera.stream.removeTrack(from);
    camera.stream.addTrack(to);
    camera.videoTrack = to;
    setSelfStream(new MediaStream(camera.stream.getTracks()));
  }, []);

  /** Blurs the camera's background, or goes back to the plain camera. Returns
   * whether it ended up as asked. */
  const setBlur = useCallback(
    async (on: boolean): Promise<boolean> => {
      const camera = cameraRef.current;
      if (!camera) return false;
      // With the camera closed there is nothing to blur; the choice is kept
      // and applied when the camera opens again.
      if (offTrackRef.current) return true;
      if (on) {
        if (blurRef.current) return true;
        const raw = camera.videoTrack;
        if (!raw) {
          showNotice("Turn your camera on first.");
          return false;
        }
        let handle: BlurHandle;
        setControls((prev) => ({ ...prev, blurLoading: true }));
        try {
          handle = await createBlur(raw);
        } catch {
          showNotice("Background blur is not available here.");
          return false;
        } finally {
          setControls((prev) => ({ ...prev, blurLoading: false }));
        }
        if (cameraRef.current !== camera || camera.videoTrack !== raw) {
          handle.stop();
          return false;
        }
        handle.track.enabled = raw.enabled;
        blurRef.current = handle;
        swapVideo(camera, raw, handle.track);
        return true;
      }
      const handle = blurRef.current;
      if (!handle) return true;
      const raw = handle.source;
      raw.enabled = handle.track.enabled;
      blurRef.current = null;
      swapVideo(camera, handle.track, raw);
      handle.stop();
      return true;
    },
    [showNotice, swapVideo],
  );

  const toggleBlur = useCallback(() => {
    const next = !controlsRef.current.blurOn;
    setControls((prev) => ({ ...prev, blurOn: next }));
    setBlurEnabled(next);
    void setBlur(next).then((ok) => {
      if (ok) return;
      setControls((prev) => ({ ...prev, blurOn: !next }));
      setBlurEnabled(!next);
    });
  }, [setBlur]);

  const toggleCamera = useCallback(() => {
    const next = !controlsRef.current.cameraOn;
    const track = cameraRef.current?.videoTrack;
    if (next && !track) {
      void addMissingTrack("video").then((ok) => {
        if (!ok) return;
        setControls((prev) => ({ ...prev, cameraOn: true }));
        if (selfIdRef.current) socketRef.current?.send({ type: "updateState", cameraOff: false });
        if (controlsRef.current.blurOn) void setBlur(true);
      });
      return;
    }
    const camera = cameraRef.current;
    if (!camera || !track) {
      if (selfIdRef.current) socketRef.current?.send({ type: "updateState", cameraOff: !next });
      setControls((prev) => ({ ...prev, cameraOn: next }));
      return;
    }
    // Off closes the camera itself, so its light goes out; a track that is
    // only disabled keeps the device open. A black track stands in so the
    // published sender never changes, and on opens the camera again.
    if (!next) {
      if (cameraOpeningRef.current || offTrackRef.current) return;
      const blur = blurRef.current;
      const raw = blur ? blur.source : track;
      const off = createOffTrack();
      offTrackRef.current = off;
      swapVideo(camera, track, off.track);
      if (blur) {
        blurRef.current = null;
        blur.stop();
      }
      raw.stop();
      if (selfIdRef.current) socketRef.current?.send({ type: "updateState", cameraOff: true });
      setControls((prev) => ({ ...prev, cameraOn: false }));
      return;
    }
    const off = offTrackRef.current;
    if (!off || cameraOpeningRef.current) return;
    cameraOpeningRef.current = true;
    void (async () => {
      try {
        let handles: CameraHandles;
        try {
          handles = await openCamera({ video: true, audio: false, facingMode: facingRef.current });
        } catch (err) {
          const denied = (err as { name?: string } | null)?.name === "NotAllowedError";
          showNotice(
            denied ? "Your camera is blocked. Allow it for this site in your browser's settings." : "No camera found.",
          );
          return;
        }
        const fresh = handles.videoTrack;
        if (!fresh || cameraRef.current !== camera || offTrackRef.current !== off || mediaReleasedRef.current) {
          stopStream(handles.stream);
          return;
        }
        swapVideo(camera, off.track, fresh);
        off.stop();
        offTrackRef.current = null;
        if (selfIdRef.current) socketRef.current?.send({ type: "updateState", cameraOff: false });
        setControls((prev) => ({ ...prev, cameraOn: true }));
        if (controlsRef.current.blurOn) void setBlur(true);
      } finally {
        cameraOpeningRef.current = false;
      }
    })();
  }, [addMissingTrack, setBlur, swapVideo, showNotice]);

  const toggleOriginalSound = useCallback(() => {
    const next = !originalSoundOnRef.current;
    originalSoundOnRef.current = next;
    lastSoundToggleRef.current = Date.now();
    setControls((prev) => ({ ...prev, originalSoundOn: next }));
    const session = sessionRef.current;
    if (!session) return;
    // Republish the mic with the new setting and announce the new track and
    // the flag in one message (see CallSession.setOriginalSound for why a
    // republish rather than a renegotiation).
    session
      .setOriginalSound(next)
      .then((published) => {
        if (published) {
          midsRef.current.audio = published.mid;
          socketRef.current?.send({
            type: "publishTrack",
            kind: "audio",
            sessionId: session.id,
            trackName: published.trackName,
            originalSound: next,
          });
        } else {
          socketRef.current?.send({ type: "updateState", originalSound: next });
        }
      })
      .catch((err: unknown) => {
        console.error("Failed to switch original sound:", err);
        originalSoundOnRef.current = !next;
        setControls((prev) => ({ ...prev, originalSoundOn: !next }));
      });
  }, []);
  followOriginalSoundRef.current = (on: boolean) => {
    if (originalSoundOnRef.current === on || Date.now() - lastSoundToggleRef.current < 3000) return;
    toggleOriginalSound();
  };

  const toggleHand = useCallback(() => {
    const next = !controlsRef.current.handRaised;
    setControls((prev) => ({ ...prev, handRaised: next }));
    socketRef.current?.send({ type: "handSet", raised: next });
  }, []);

  const reportTrivia = useCallback((event: TriviaEvent) => {
    socketRef.current?.sendLoose({ type: "trivia", ...event });
  }, []);

  const reportSelfView = useCallback((report: VideoFitReport) => {
    socketRef.current?.sendLoose({ ...report, type: "selfview" });
  }, []);

  const lowerHand = useCallback((participantId: string) => {
    socketRef.current?.send({ type: "handSet", raised: false, participantId });
  }, []);

  const toggleScreenShare = useCallback(() => {
    setControls((prev) => {
      if (prev.sharingScreen) {
        stopStream(screenStreamRef.current);
        screenStreamRef.current = null;
        setScreenStream(null);
        if (midsRef.current.screen) void sessionRef.current?.unpublish(midsRef.current.screen);
        midsRef.current.screen = null;
        socketRef.current?.send({ type: "unpublishTrack", kind: "screen" });
        return { ...prev, sharingScreen: false };
      }
      void (async () => {
        try {
          const stream = await openScreenShare();
          screenStreamRef.current = stream;
          setScreenStream(stream);
          const track = stream.getVideoTracks()[0];
          const session = sessionRef.current;
          if (track && session) {
            const published = await session.publish(track, "screen");
            midsRef.current.screen = published.mid;
            socketRef.current?.send({ type: "publishTrack", kind: "screen", sessionId: session.id, trackName: published.trackName });
            track.addEventListener("ended", () => toggleScreenShare());
          }
        } catch {
          // The user dismissed the share picker. Nothing to do.
        }
      })();
      return { ...prev, sharingScreen: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flipCamera = useCallback(() => {
    if (flippingRef.current) return;
    flippingRef.current = true;
    // The tile turns edge-on first, so the mirror change and the camera swap
    // happen while nothing is visible; then the new camera turns into view.
    setFlipPhase("out");
    const turnedAway = new Promise((resolve) => setTimeout(resolve, FLIP_OUT_MS));
    const turnBack = (): void => {
      setFlipPhase("in");
      setTimeout(() => {
        setFlipPhase("idle");
        flippingRef.current = false;
      }, FLIP_IN_MS);
    };
    void (async () => {
      const nextFacing = facingRef.current === "user" ? "environment" : "user";
      try {
        if (offTrackRef.current) {
          // The camera is closed; the next time it opens it uses this side.
          await turnedAway;
          facingRef.current = nextFacing;
          setFacing(nextFacing);
          turnBack();
          return;
        }
        const [swapped] = await Promise.all([
          openCamera({ video: true, audio: false, facingMode: nextFacing }),
          turnedAway,
        ]);
        const newTrack = swapped.videoTrack;
        if (!newTrack || !cameraRef.current) {
          stopStream(swapped.stream);
          turnBack();
          return;
        }
        facingRef.current = nextFacing;
        setFacing(nextFacing);
        const blur = blurRef.current;
        if (blur) {
          // The published track stays the blur's canvas; only what it reads changes.
          const oldRaw = blur.source;
          newTrack.enabled = oldRaw.enabled;
          blur.setSource(newTrack);
          oldRaw.stop();
          turnBack();
          return;
        }
        sessionRef.current?.replaceVideoTrack(newTrack);
        const oldTrack = cameraRef.current.videoTrack;
        if (oldTrack) {
          cameraRef.current.stream.removeTrack(oldTrack);
          oldTrack.stop();
        }
        cameraRef.current.stream.addTrack(newTrack);
        cameraRef.current.videoTrack = newTrack;
        setSelfStream(cameraRef.current.stream);
      } catch {
        // No second camera reachable right now; stay on the current one.
      }
      turnBack();
    })();
  }, []);

  /** Flips the host's own view at once and asks the room; see the
   * `recording` field's doc comment. Stopping opens "Recording saved". */
  const toggleRecording = useCallback(() => {
    // Record on a quiet recording tells the room instead of stopping it.
    const next = recordingQuiet ? true : !recording;
    setRecording(next);
    setRecordingQuiet(false);
    socketRef.current?.send({ type: "setRecording", recording: next });
    if (!next) {
      savedHeldRef.current = false;
      setRecordingSaved({ recordingId: recordingIdRef.current, phase: "saved" });
      recordingIdRef.current = null;
      closeSavedIn(SAVED_POPUP_MS);
    }
  }, [closeSavedIn, recording, recordingQuiet]);

  const deleteQuietRecording = useCallback(() => {
    const id = recordingIdRef.current;
    if (!id) return;
    socketRef.current?.send({ type: "discardRecording", recordingId: id });
    setRecordingQuiet(false);
    recordingIdRef.current = null;
    showNotice("Recording deleted.");
  }, [showNotice]);

  const discardRecording = useCallback(() => {
    const id = recordingSaved?.recordingId;
    if (!id || recordingSaved.phase !== "saved") return;
    socketRef.current?.send({ type: "discardRecording", recordingId: id });
    setRecordingSaved({ recordingId: id, phase: "deleting" });
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    // The room answers in a moment; if the answer is lost, still close.
    savedHeldRef.current = false;
    closeSavedIn(5000);
  }, [closeSavedIn, recordingSaved]);

  /** Holding a finger or pointer on the popup keeps it open. */
  const holdRecordingSaved = useCallback(
    (held: boolean) => {
      savedHeldRef.current = held;
      if (!held) closeSavedIn(1200);
    },
    [closeSavedIn],
  );

  const respondToKnock = useCallback((knockId: string, allow: boolean) => {
    socketRef.current?.send({ type: "knockRespond", knockId, allow });
    setPendingKnocks((prev) => prev.filter((k) => k.knockId !== knockId));
  }, []);

  const leave = useCallback(() => {
    // What the call looked like at the moment of Leave (QUEUE row 337), sent
    // ahead of the leave itself on the same socket.
    if (selfIdRef.current) {
      socketRef.current?.sendLoose({
        type: "dropReport",
        outcome: "leaving",
        trigger: "unknown",
        awayMs: 0,
        attempts: 0,
        platform: "web",
        context: leaveContext({
          pc: sessionRef.current?.pc ?? null,
          mic: cameraRef.current?.audioTrack ?? null,
          camera: cameraRef.current?.videoTrack ?? null,
          recent: recentRef.current,
          joinedAt: joinedAtRef.current,
          videoIn: videoInRef.current,
        }),
      });
    }
    socketRef.current?.leave();
    sessionRef.current?.close();
    releaseCamera();
    stopStream(screenStreamRef.current);
    screenStreamRef.current = null;
    dispatch({ type: "LEAVE" });
  }, [dispatch]);

  return {
    state,
    room,
    revealed,
    noteMediaReady,
    selfId,
    selfStream,
    screenStream,
    remoteTiles: Object.values(remoteTiles),
    pendingKnocks,
    controls,
    toggleMic,
    toggleCamera,
    toggleOriginalSound,
    toggleHand,
    lowerHand,
    reportTrivia,
    reportSelfView,
    toggleScreenShare,
    respondToKnock,
    flipCamera,
    toggleBlur,
    facing,
    flipPhase,
    leave,
    recording,
    joinedAt,
    recordArmed,
    recordingQuiet,
    deleteQuietRecording,
    toggleRecording,
    recordingSaved,
    discardRecording,
    holdRecordingSaved,
    notice,
    showNotice,
    mediaAlert,
    panel,
    chat,
    assistant,
  };
}
